import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Box,
	Flex,
	HStack,
	Text,
	Icon,
	IconButton,
	Image,
	Spinner,
} from '@chakra-ui/react';
import { FiX, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import useMediaObjectUrl, { MEDIA_ERROR_TEXT } from '../hooks/useMediaObjectUrl';
import { getMedia } from './mediaRegistry';
import { full } from './time';

/**
 * Full-screen viewer for a thread's photos and videos, with prev/next through
 * everything the thread has loaded — the way tapping a photo works in
 * WhatsApp.
 *
 * Bytes come from the media registry when the bubble already fetched them,
 * and through the authenticated proxy otherwise (a photo further up than the
 * viewport has been). Swipe on touch, arrows on a keyboard, and a download
 * that names the file the way the sender did.
 */
function Stage({ conversationId, item }) {
	const cached = getMedia(item.wamid);
	const fetched = useMediaObjectUrl({
		conversationId,
		wamid: item.wamid,
		enabled: !cached && Boolean(item.wamid),
	});
	const url = cached?.url ?? fetched.url;
	const status = cached ? 'ready' : fetched.status;

	if (status === 'error') {
		return (
			<Text color="whiteAlpha.800" fontSize="sm">
				{MEDIA_ERROR_TEXT[fetched.error] ?? 'This file could not be loaded.'}
			</Text>
		);
	}
	if (status !== 'ready' || !url) return <Spinner color="whiteAlpha.800" />;

	if (item.type === 'video') {
		return (
			<Box as="video" src={url} controls autoPlay maxH="82vh" maxW="92vw" outline="none" onClick={(e) => e.stopPropagation()} />
		);
	}
	return (
		<Image
			src={url}
			alt={item.media?.caption || item.media?.filename || 'Photo'}
			maxH="82vh"
			maxW="92vw"
			objectFit="contain"
			onClick={(e) => e.stopPropagation()}
			draggable={false}
		/>
	);
}

export default function MediaLightbox({ isOpen, onClose, items, index, onIndexChange, conversationId }) {
	const item = items[index];
	const hasPrev = index > 0;
	const hasNext = index < items.length - 1;
	const start = React.useRef(null);

	React.useEffect(() => {
		if (!isOpen) return undefined;
		const onKey = (e) => {
			if (e.key === 'ArrowLeft' && hasPrev) onIndexChange(index - 1);
			if (e.key === 'ArrowRight' && hasNext) onIndexChange(index + 1);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isOpen, index, hasPrev, hasNext, onIndexChange]);

	const download = () => {
		const url = getMedia(item?.wamid)?.url;
		if (!url) return;
		const a = document.createElement('a');
		a.href = url;
		a.download = item.media?.filename || `whatsapp-${item.type}-${item.wamid}`;
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	if (!item) return null;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="scale">
			<ModalOverlay bg="wa.overlay" />
			<ModalContent bg="transparent" boxShadow="none" m="0" onClick={onClose}>
				<Flex
					px="4"
					py="3"
					align="center"
					justify="space-between"
					color="whiteAlpha.900"
					onClick={(e) => e.stopPropagation()}
				>
					<Box minW="0">
						<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
							{item.media?.filename || (item.type === 'video' ? 'Video' : 'Photo')}
						</Text>
						<Text fontSize="xs" color="whiteAlpha.700">
							{full(item.occurredAt)} · {index + 1} of {items.length}
						</Text>
					</Box>
					<HStack spacing="1">
						<IconButton
							aria-label="Download"
							icon={<Icon as={FiDownload} />}
							variant="ghost"
							color="whiteAlpha.900"
							_hover={{ bg: 'whiteAlpha.200' }}
							onClick={download}
						/>
						<IconButton
							aria-label="Close"
							icon={<Icon as={FiX} boxSize="5" />}
							variant="ghost"
							color="whiteAlpha.900"
							_hover={{ bg: 'whiteAlpha.200' }}
							onClick={onClose}
						/>
					</HStack>
				</Flex>

				<ModalBody
					display="flex"
					alignItems="center"
					justifyContent="center"
					p="0"
					position="relative"
					h="calc(100vh - 64px)"
					onPointerDown={(e) => {
						start.current = { x: e.clientX, y: e.clientY };
					}}
					onPointerUp={(e) => {
						if (!start.current) return;
						const dx = e.clientX - start.current.x;
						const dy = e.clientY - start.current.y;
						start.current = null;
						if (Math.abs(dx) < 48 || Math.abs(dy) > Math.abs(dx)) return;
						if (dx < 0 && hasNext) onIndexChange(index + 1);
						if (dx > 0 && hasPrev) onIndexChange(index - 1);
					}}
				>
					{hasPrev && (
						<IconButton
							aria-label="Previous"
							icon={<Icon as={FiChevronLeft} boxSize="6" />}
							position="absolute"
							left="3"
							borderRadius="full"
							bg="whiteAlpha.200"
							color="whiteAlpha.900"
							_hover={{ bg: 'whiteAlpha.300' }}
							onClick={(e) => {
								e.stopPropagation();
								onIndexChange(index - 1);
							}}
						/>
					)}
					<Stage key={item.wamid} conversationId={conversationId} item={item} />
					{hasNext && (
						<IconButton
							aria-label="Next"
							icon={<Icon as={FiChevronRight} boxSize="6" />}
							position="absolute"
							right="3"
							borderRadius="full"
							bg="whiteAlpha.200"
							color="whiteAlpha.900"
							_hover={{ bg: 'whiteAlpha.300' }}
							onClick={(e) => {
								e.stopPropagation();
								onIndexChange(index + 1);
							}}
						/>
					)}
					{item.media?.caption && (
						<Text
							position="absolute"
							bottom="4"
							left="50%"
							transform="translateX(-50%)"
							maxW="80vw"
							color="whiteAlpha.900"
							fontSize="sm"
							bg="blackAlpha.500"
							px="3"
							py="1.5"
							borderRadius="md"
							textAlign="center"
						>
							{item.media.caption}
						</Text>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
